/**
 * efood: παράθυρο χρόνου ετοιμασίας — αντίγραφο του backend/utils/efoodPrepWindow.js.
 * Κρατήστε τα δύο συγχρονισμένα.
 *
 * ΕΔΩ χρησιμεύει ΜΟΝΟ για να γκριζάρουμε κουμπιά. Η αυθεντία είναι ο server:
 * ό,τι κι αν σταλεί, το κόβει μέσα στο παράθυρο πριν φύγει στο efood — οπότε
 * ένα ξεπερασμένο bundle δεν μπορεί να στείλει άκυρο timestamp.
 */

const EARLIEST_OFFSET_MIN = -4;
const LATEST_OFFSET_MIN = 16;
const MIN_MINUTES_FROM_NOW = 3;
const MIN = 60 * 1000;

export interface PrepWindow {
  /** Τι προτείνει το efood, σε λεπτά */
  prep: number;
  /** Πότε υπολογίζει το efood ότι θα είναι έτοιμο */
  anchor: Date;
  earliest: Date;
  latest: Date;
  /** Επιτρεπτό εύρος «λεπτά από τώρα» */
  minFromNow: number;
  maxFromNow: number;
  expired: boolean;
}

const parseDate = (v: any): Date | null => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const efoodOrder = (orderJson: any): any => {
  if (!orderJson) return null;
  if (Array.isArray(orderJson.orders)) return orderJson.orders[0] || null;
  return orderJson;
};

/**
 * Το παράθυρο μιας efood παραγγελίας, ή null όταν δεν εφαρμόζεται
 * (vendor_delivery — αυτές οι παραγγελίες δεν φέρνουν preparation_time_minutes).
 */
export function prepWindow(orderJson: any, now: Date = new Date()): PrepWindow | null {
  const o = efoodOrder(orderJson);
  if (!o) return null;

  const prep = parseInt(o.preparation_time_minutes, 10);
  if (!isFinite(prep) || prep <= 0) return null;

  const placed = parseDate(o.timestamp);
  const riderPickup = parseDate(o.rider_pickup_timestamp);
  if (!placed && !riderPickup) return null;

  // Άγκυρα το ΖΩΝΤΑΝΟ σχέδιο παραλαβής του efood (rider_pickup_timestamp) — σε
  // καθυστερημένες παραδόσεις webhook το timestamp+prep είναι μπαγιάτικο και η
  // παλιά «τομή» έβγαζε κενό παράθυρο: όλα τα κουμπιά γκρι (Περιστέρι, 31/8).
  const anchor = riderPickup || new Date((placed as Date).getTime() + prep * MIN);

  const earliest = new Date(anchor.getTime() + EARLIEST_OFFSET_MIN * MIN);
  const latest = new Date(anchor.getTime() + LATEST_OFFSET_MIN * MIN);

  const minFromNow = Math.max(MIN_MINUTES_FROM_NOW, Math.ceil((earliest.getTime() - now.getTime()) / MIN));
  const maxFromNow = Math.floor((latest.getTime() - now.getTime()) / MIN);

  return {
    prep,
    anchor,
    earliest,
    latest,
    minFromNow,
    maxFromNow,
    expired: maxFromNow < minFromNow,
  };
}

/** «Έτοιμο σε X′» → ώρα ρολογιού, για να μην υπάρχει παρεξήγηση. */
export function clockAfter(minutes: number, now: Date = new Date()): string {
  return new Date(now.getTime() + minutes * MIN)
    .toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
}

/** Το preset που είναι πιο κοντά στην πρόταση του efood — παίρνει το badge. */
export function suggestedPreset(presets: number[], w: PrepWindow | null): number | null {
  if (!w || !presets.length) return null;
  const inRange = presets.filter(p => p >= w.minFromNow && p <= w.maxFromNow);
  if (!inRange.length) return null;
  return inRange.reduce((best, p) =>
    Math.abs(p - w.prep) < Math.abs(best - w.prep) ? p : best, inRange[0]);
}
